package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import co.uk.sacursoftware.ssinventory.util.getCurrentLocalDate
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ToolsScreen(
    onBack: () -> Unit,
    onNavigateToAddEdit: (ToolRequest?) -> Unit
) {
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }

    var tools by remember { mutableStateOf<List<ToolRequest>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var selectedTab by remember { mutableStateOf(0) } // 0: Open, 1: Closed, 2: Deleted
    var toolToDelete by remember { mutableStateOf<ToolRequest?>(null) }

    fun loadTools() {
        scope.launch {
            isLoading = true
            repository.getTools().onSuccess { list ->
                tools = list.sortedBy { it.deadlineDate }
            }.onFailure {
                tools = emptyList()
            }
            isLoading = false
        }
    }

    LaunchedEffect(Unit) {
        loadTools()
    }

    val openTools = tools.filter { it.isDeleted != 1 && it.completedDate.isNullOrBlank() }
    val closedTools = tools.filter { it.isDeleted != 1 && !it.completedDate.isNullOrBlank() }
    val deletedTools = tools.filter { it.isDeleted == 1 }

    val currentList = when (selectedTab) {
        0 -> openTools
        1 -> closedTools
        else -> deletedTools
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TopAppBar(
                title = { ScreenTitle("Tools & Equipment Requests") },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        BackArrowIcon()
                    }
                },
                actions = {
                    ToolbarPlusButton(onClick = { onNavigateToAddEdit(null) })
                }
            )
        }
    ) { padding ->
        Box(modifier = Modifier.padding(padding).fillMaxSize()) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                TabRow(selectedTabIndex = selectedTab) {
                    Tab(selected = selectedTab == 0, onClick = { selectedTab = 0 }, text = { Text("Open (${openTools.size})") })
                    Tab(selected = selectedTab == 1, onClick = { selectedTab = 1 }, text = { Text("Closed (${closedTools.size})") })
                    Tab(selected = selectedTab == 2, onClick = { selectedTab = 2 }, text = { Text("Deleted (${deletedTools.size})") })
                }

                if (isLoading) {
                    Box(modifier = Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) {
                        BrandLoadingSpinner()
                    }
                } else if (currentList.isEmpty()) {
                    Box(modifier = Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(8.dp)) {
                            Text("No items found in this tab.", style = MaterialTheme.typography.bodyLarge, color = MaterialTheme.colorScheme.outline)
                            if (selectedTab == 0) {
                                Button(onClick = { onNavigateToAddEdit(null) }) {
                                    Text("Create First Tool Request")
                                }
                            }
                        }
                    }
                } else {
                    LazyColumn(
                        modifier = Modifier.fillMaxWidth().weight(1f),
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        items(items = currentList, key = { it.id }) { tool ->
                            ToolItemCard(
                                tool = tool,
                                onEdit = { onNavigateToAddEdit(tool) },
                                onComplete = {
                                    scope.launch {
                                        val todayStr = getCurrentLocalDate().toString()
                                        val req = SaveToolRequest(
                                            id = tool.id,
                                            title = tool.title,
                                            description = tool.description,
                                            deadlineDate = tool.deadlineDate,
                                            toolName = tool.toolName,
                                            completedDate = todayStr,
                                            isDeleted = 0,
                                            images = tool.images
                                        )
                                        repository.saveTool(req).onSuccess { loadTools() }
                                    }
                                },
                                onDelete = { toolToDelete = tool },
                                onRestore = {
                                    scope.launch {
                                        val req = SaveToolRequest(
                                            id = tool.id,
                                            title = tool.title,
                                            description = tool.description,
                                            deadlineDate = tool.deadlineDate,
                                            toolName = tool.toolName,
                                            completedDate = null,
                                            isDeleted = 0,
                                            images = tool.images
                                        )
                                        repository.saveTool(req).onSuccess { loadTools() }
                                    }
                                }
                            )
                        }
                    }
                }
            }
        }
    }

    toolToDelete?.let { tool ->
        ConfirmDeleteDialog(
            message = "Delete '${tool.title}'?",
            onConfirm = {
                val toDelete = tool
                toolToDelete = null
                scope.launch {
                    val req = SaveToolRequest(
                        id = toDelete.id,
                        title = toDelete.title,
                        description = toDelete.description,
                        deadlineDate = toDelete.deadlineDate,
                        toolName = toDelete.toolName,
                        completedDate = toDelete.completedDate,
                        isDeleted = 1,
                        images = toDelete.images
                    )
                    repository.saveTool(req).onSuccess { loadTools() }
                }
            },
            onDismiss = { toolToDelete = null }
        )
    }
}

@Composable
fun ToolItemCard(
    tool: ToolRequest,
    onEdit: () -> Unit,
    onComplete: () -> Unit,
    onDelete: () -> Unit,
    onRestore: () -> Unit
) {
    val isClosed = !tool.completedDate.isNullOrBlank()
    val isDeleted = tool.isDeleted == 1

    Card(
        modifier = Modifier.fillMaxWidth().clickable(onClick = onEdit),
        shape = MaterialTheme.shapes.medium,
        colors = CardDefaults.cardColors(
            containerColor = when {
                isDeleted -> MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f)
                isClosed -> successContainerColor()
                else -> MaterialTheme.colorScheme.surface
            }
        ),
        border = BorderStroke(1.dp, when {
            isDeleted -> MaterialTheme.colorScheme.outline.copy(alpha = 0.2f)
            isClosed -> successContentColor()
            else -> MaterialTheme.colorScheme.outline.copy(alpha = 0.3f)
        })
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(16.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text(
                    text = tool.title,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold
                )
                Text(
                    text = "Tool: ${tool.toolName} | Deadline: ${tool.deadlineDate}",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.secondary
                )
                if (!tool.description.isNullOrBlank()) {
                    Text(
                        text = tool.description,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.outline
                    )
                }
                if (isClosed) {
                    Text(
                        text = "Completed: ${tool.completedDate}",
                        style = MaterialTheme.typography.labelSmall,
                        color = successContentColor(),
                        fontWeight = FontWeight.Bold
                    )
                }
            }

            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                if (isDeleted) {
                    TextButton(onClick = onRestore, contentPadding = PaddingValues(4.dp)) {
                        Text("Restore", style = MaterialTheme.typography.labelSmall)
                    }
                } else if (isClosed) {
                    TextButton(onClick = onRestore, contentPadding = PaddingValues(4.dp)) {
                        Text("Reopen", style = MaterialTheme.typography.labelSmall)
                    }
                    IconButton(onClick = onDelete) {
                        CloseIcon(tint = MaterialTheme.colorScheme.error)
                    }
                } else {
                    Button(onClick = onComplete, shape = MaterialTheme.shapes.small, contentPadding = PaddingValues(horizontal = 8.dp, vertical = 2.dp)) {
                        Text("Complete", style = MaterialTheme.typography.labelSmall)
                    }
                    IconButton(onClick = onDelete) {
                        CloseIcon(tint = MaterialTheme.colorScheme.error)
                    }
                }
            }
        }
    }
}
